import { Button, addToast, useMobile } from '@octano/global-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HTTP_STATUS_CODE } from '../../../../api/request';
import { downloadFromURL } from '../../../../utils/blob';
import { CertificateType } from '../../types';
import DynamicFormModal from '../DynamicFormModal/DynamicFormModal';
import GeneratingFileModal from '../GeneratingFileModal';
import NoResultsModal from '../NoResultsModal';
import NotRegularStModal from '../NotRegularStModal';
import {
  createReportDownloadHandler,
  createCertificateWithCustomFieldsHandler,
} from './handlerCreator';
import NotInterimGraduatedModal from '../NotInterimGraduated';
import CeNotCompletedModal from '../CeNotCompletedModal';

enum ModalType {
  NONE,
  DYNAMIC_FORM,
  GENERATING_FILE,
  NO_CERTIFICATE,
  NOT_REGULAR_STUDENT,
  NOT_GRADUATED_INTERIM,
  CE_NOT_COMPLETED,
}

interface ICertificateActionsProps {
  studyPlanEnrollmentId: number;
  report: CertificateType;
  isDynamic?: boolean;
}

export default function CertificateActions({
  studyPlanEnrollmentId,
  report,
  isDynamic = false,
}: ICertificateActionsProps) {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const [openedModal, setOpenedModal] = useState<ModalType>(ModalType.NONE);

  const handleReportDownload = createReportDownloadHandler({
    beforeDownload: () => setOpenedModal(ModalType.GENERATING_FILE),
    afterDownload: ({ status }: { url: string; status: number }) => {
      if (status === HTTP_STATUS_CODE.NO_CONTENT) {
        setOpenedModal(ModalType.NO_CERTIFICATE);
      } else {
        setOpenedModal(ModalType.NONE);
      }
    },
    onSuccess: ({ url }) => {
      if (url) {
        downloadFromURL(url);
      }
    },
    onError: ({ data, status }) => {
      if (data?.error === 'NOT_REGULAR_STUDENT') {
        setOpenedModal(ModalType.NOT_REGULAR_STUDENT);
      } else if (data?.error === 'CE_NOT_COMPLETED') {
        setOpenedModal(ModalType.CE_NOT_COMPLETED);
      } else if (status !== HTTP_STATUS_CODE.NO_CONTENT) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`reports.errors.errorGeneratingFile`),
        });
        setOpenedModal(ModalType.NONE);
      }
    },
  });

  const handleCertificateGeneration = createCertificateWithCustomFieldsHandler({
    beforeDownload: () => setOpenedModal(ModalType.GENERATING_FILE),
    afterDownload: ({
      status,
      error,
    }: {
      url: string;
      status: number;
      error?: any;
    }) => {
      const errorData = error?.data;

      if (errorData?.error === 'NOT_GRADUATED_INTERIM') {
        setOpenedModal(ModalType.NOT_GRADUATED_INTERIM);
      } else {
        if (status === HTTP_STATUS_CODE.NO_CONTENT) {
          setOpenedModal(ModalType.NO_CERTIFICATE);
        } else {
          setOpenedModal(ModalType.NONE);
        }
      }
    },
    onSuccess: ({ url }) => {
      if (url) {
        downloadFromURL(url);
      }
      setOpenedModal(ModalType.NONE);
    },
    onError: ({ data }) => {
      if (data?.error === 'NOT_REGULAR_STUDENT') {
        setOpenedModal(ModalType.NOT_REGULAR_STUDENT);
      } else if (data?.error === 'NOT_GRADUATED_INTERIM') {
        // El modal se maneja en afterDownload, aquí solo evitamos el toast
      } else if (data?.error === 'CE_NOT_COMPLETED') {
        setOpenedModal(ModalType.CE_NOT_COMPLETED);
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`reports.errors.errorGeneratingFile`),
        });
        setOpenedModal(ModalType.NONE);
      }
    },
  });

  const getActionHandler = () => {
    if (isDynamic) {
      return () => setOpenedModal(ModalType.DYNAMIC_FORM);
    }

    return () => handleReportDownload(studyPlanEnrollmentId, report.code);
  };

  const getButtonText = () => {
    if (isDynamic) {
      return t(`reports.actions.generateCustom`);
    }
    return t(`common.actions.download`);
  };

  const handleFormSubmit = (formData: Record<string, any>) => {
    handleCertificateGeneration(studyPlanEnrollmentId, report.code, formData);
  };

  const handleFormCancel = () => {
    setOpenedModal(ModalType.NONE);
  };

  const handleNoResultsConfirm = () => {
    setOpenedModal(ModalType.NONE);
  };

  const handleNotInterimGraduatedConfirm = () => {
    setOpenedModal(ModalType.NONE);
  };

  const handleCeNotCompletedConfirm = () => {
    setOpenedModal(ModalType.NONE);
  };

  return (
    <>
      <DynamicFormModal
        isOpen={openedModal === ModalType.DYNAMIC_FORM}
        certificateTypeCode={report.code}
        certificateTitle={report.name}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        studyPlanEnrollmentId={studyPlanEnrollmentId}
      />

      <NotRegularStModal
        isOpen={openedModal === ModalType.NOT_REGULAR_STUDENT}
      />

      <NotInterimGraduatedModal
        isOpen={openedModal === ModalType.NOT_GRADUATED_INTERIM}
        onConfirm={handleNotInterimGraduatedConfirm}
      />

      <CeNotCompletedModal
        isOpen={openedModal === ModalType.CE_NOT_COMPLETED}
        onConfirm={handleCeNotCompletedConfirm}
      />

      <GeneratingFileModal isOpen={openedModal === ModalType.GENERATING_FILE} />

      <NoResultsModal
        isOpen={openedModal === ModalType.NO_CERTIFICATE}
        onConfirm={handleNoResultsConfirm}
        texts={{
          title: t(`reports.enrolledStudent.noResults.description`),
          description: '',
        }}
      />

      <div
        className="d-flex flex-wrap align-items-center justify-content-center"
        style={{ gap: 12 }}
      >
        <Button
          text={getButtonText()}
          icon="download"
          size="sm"
          outlined
          style={{ width: isMobile ? '90%' : 123 }}
          onClick={getActionHandler()}
        />
      </div>
    </>
  );
}
